class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.isOpen = false;
    this.searchTerm = '';
    this.abortController = new AbortController();
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    if (!this.input || !this.predictiveSearchResults) return;

    this.input.addEventListener('input', this.debounce((e) => this.onChange(e), 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.form && this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    /* Move the dropdown to <body> so no parent stacking context clips it */
    this._dropdown = this.predictiveSearchResults;
    document.body.appendChild(this._dropdown);
    this._dropdown.style.cssText = [
      'display:none',
      'position:fixed',
      'z-index:2147483647',
      'background:#fff',
      'border-radius:12px',
      'border:1px solid rgba(0,0,0,0.10)',
      'box-shadow:0 12px 40px rgba(0,0,0,0.15)',
      'overflow-y:auto',
      'max-height:380px',
      '-webkit-overflow-scrolling:touch'
    ].join(';');
  }

  disconnectedCallback() {
    if (this._dropdown && this._dropdown.parentNode === document.body) {
      document.body.removeChild(this._dropdown);
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  positionDropdown() {
    if (!this._dropdown || !this.input) return;
    const form = this.input.closest('form');
    const anchor = form || this.input;
    const rect = anchor.getBoundingClientRect();
    this._dropdown.style.top = (rect.bottom + 6) + 'px';
    this._dropdown.style.left = rect.left + 'px';
    this._dropdown.style.width = rect.width + 'px';
    this._dropdown.style.display = 'block';
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this._dropdown.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) { this.close(true); return; }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this._dropdown.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.isOpen) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this._dropdown.contains(document.activeElement)) {
        this.close();
      }
    }, 150);
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
      case 'Escape': this.close(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this._dropdown.querySelector('[data-predictive-search-search-for-text]');
    if (el && previousTerm) {
      const matches = el.innerText.match(new RegExp(previousTerm, 'g'));
      if (matches && matches.length === 1) el.innerText = el.innerText.replace(previousTerm, newTerm);
    }
  }

  switchOption(direction) {
    if (!this.isOpen) return;
    const moveUp = direction === 'up';
    const selectedElement = this._dropdown.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this._dropdown.querySelectorAll('li, button.predictive-search__item')).filter(el => el.offsetParent !== null);
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    let activeElementIndex = 0;
    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this._dropdown.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setAttribute('loading', true);

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then(r => { if (!r.ok) { this.close(); throw new Error(r.status); } return r.text(); })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach(inst => { inst.cachedResults[queryKey] = resultsMarkup; });
        this.renderSearchResults(resultsMarkup);
      })
      .catch(error => { if (error?.code !== 20) { this.close(); throw error; } });
  }

  renderSearchResults(resultsMarkup) {
    this._dropdown.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  open() {
    this.positionDropdown();
    this.setAttribute('open', true);
    if (this.input) this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;

    /* Reposition on scroll/resize */
    this._reposition = () => { if (this.isOpen) this.positionDropdown(); };
    window.addEventListener('scroll', this._reposition, { passive: true });
    window.addEventListener('resize', this._reposition, { passive: true });
  }

  close(clearSearchTerm = false) {
    this.isOpen = false;
    this.removeAttribute('open');
    this.removeAttribute('loading');
    if (this._dropdown) this._dropdown.style.display = 'none';
    if (this.input) {
      if (clearSearchTerm) this.input.value = '';
      this.input.setAttribute('aria-expanded', false);
      this.input.setAttribute('aria-activedescendant', '');
    }
    if (clearSearchTerm) this.removeAttribute('results');
    window.removeEventListener('scroll', this._reposition);
    window.removeEventListener('resize', this._reposition);
  }
}

customElements.define('predictive-search', PredictiveSearch);
